import type { Building } from '@invisoutlet/shared/types';
import { useEffect, useRef, useState } from 'react';

interface BuildingSearchDropdownProps {
  buildings: Building[];
  selected: string;
  onChange: (id: string) => void;
  placeholder?: string;
  nullable?: boolean;
}

export function BuildingSearchDropdown({
  buildings,
  selected,
  onChange,
  placeholder = 'Search buildings…',
  nullable = false,
}: BuildingSearchDropdownProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleMouseDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);

  useEffect(() => {
    if (open) {
      // Focus search input when popover opens
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const selectedBuilding = buildings.find((b) => b.id === selected);

  const filtered = query.trim()
    ? buildings.filter((b) => b.name.toLowerCase().includes(query.toLowerCase()))
    : buildings;

  function select(id: string) {
    onChange(id);
    setOpen(false);
    setQuery('');
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent ${
          selected
            ? 'border-accent/50 bg-accent/10 text-accent'
            : 'border-border bg-bg-surface text-text-primary hover:bg-bg-elevated'
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {selectedBuilding ? (
          <span>{selectedBuilding.name}</span>
        ) : (
          <span className="text-text-secondary">{placeholder}</span>
        )}
        <svg
          className={`h-3.5 w-3.5 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 16 16"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            d="M4 6l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      </button>

      {open && (
        <div
          className="absolute left-0 top-full z-30 mt-1 min-w-[220px] rounded-lg border border-border bg-bg-primary shadow-lg"
          role="listbox"
          aria-label="Select building"
        >
          {/* Search input */}
          <div className="border-b border-border px-3 py-2">
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              className="w-full bg-transparent text-sm text-text-primary placeholder:text-text-secondary focus:outline-none"
              aria-label="Search buildings"
            />
          </div>

          {/* All buildings option */}
          {nullable && (
            <button
              type="button"
              onClick={() => select('')}
              className={`w-full px-3 py-2 text-left text-sm transition-colors hover:bg-bg-elevated ${
                !selected ? 'font-medium text-accent' : 'text-text-secondary'
              }`}
              role="option"
              aria-selected={!selected}
            >
              All buildings
            </button>
          )}

          {/* Building list */}
          <ul className="max-h-52 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-text-secondary">No buildings found</li>
            ) : (
              filtered.map((b) => (
                <li key={b.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={b.id === selected}
                    onClick={() => select(b.id)}
                    className={`w-full px-3 py-2 text-left text-sm transition-colors hover:bg-bg-elevated ${
                      b.id === selected ? 'font-medium text-accent' : 'text-text-primary'
                    }`}
                  >
                    {b.name}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
